/**
 * @fileoverview Client-side analytics tracking using PostHog.
 *
 * PostHog is lazy-loaded by PostHogProvider and assigned to `window.posthog`.
 * This module provides typed accessor functions for the lazy-loaded instance.
 *
 * @module posthog-client
 */

import type { PostHogInstance } from "@/types/posthog";

/**
 * Cached PostHog client instance - resolved from window.posthog on first use.
 * @internal
 */
let _posthogClient: PostHogInstance | null = null;

/**
 * Get the PostHog client for browser use.
 *
 * Returns the PostHog instance that was lazy-loaded by PostHogProvider
 * and assigned to window.posthog. Returns null if PostHog hasn't loaded yet
 * or if called server-side.
 *
 * @returns The PostHog client instance, or null if unavailable
 *
 * @example
 * ```typescript
 * const posthog = getPostHogClient();
 * posthog?.capture("button_clicked", { button_id: "cta" });
 * ```
 */
export function getPostHogClient(): PostHogInstance | null {
  if (_posthogClient) {
    return _posthogClient;
  }

  if (typeof window === "undefined") {
    return null;
  }

  const instance = window.posthog;
  if (instance?.__loaded) {
    _posthogClient = instance;
    return _posthogClient;
  }

  return null;
}

/**
 * Identify a user with PostHog for personalized analytics.
 *
 * Associates analytics events with a specific user ID and
 * optional properties. Call after successful login.
 *
 * @param userId - Unique user identifier
 * @param properties - Optional user properties (email, plan, etc.)
 */
export function identifyUser(
  userId: string,
  properties?: Record<string, string | number | boolean | null>,
): void {
  const posthog = getPostHogClient();
  if (!posthog) {
    return;
  }

  try {
    posthog.identify(userId, properties);
  } catch {
    // Silently fail — analytics errors should not break UX
  }
}

/**
 * Reset user identification and start a new anonymous session.
 * Call on logout to prevent events from being attributed to the wrong user.
 */
export function resetUser(): void {
  const posthog = getPostHogClient();
  if (!posthog) {
    return;
  }

  try {
    posthog.reset();
  } catch {
    // Silently fail — analytics errors should not break UX
  }
}

// ─── Trial/billing funnel event types (WS6) ─────────────────────────────────

/** All client-side funnel events with their typed property shapes (PII-free). */
export type FunnelEventMap = {
  subscription_gate_presented: {
    reason: string;
    surface: string;
    authed: boolean;
    locale: string;
  };
  trial_cta_clicked: {
    plan_id: string;
    surface: string;
  };
  pricing_viewed: {
    source: string;
    locale: string;
    authed: boolean;
    current_tier: string;
  };
  checkout_started: {
    plan_id: string;
    checkout_kind: "trial_subscription";
    source: string;
  };
  checkout_failed: {
    code: string;
    plan_id: string;
    source: string;
  };
  checkout_returned: {
    status: "success" | "cancel" | "unknown";
  };
};

/**
 * Fire a typed trial-funnel analytics event. Non-blocking: swallows all errors
 * so a PostHog throw never breaks the user flow.
 *
 * @param event - The funnel event name
 * @param properties - PII-free typed properties for the event
 */
export function trackFunnelEvent<K extends keyof FunnelEventMap>(
  event: K,
  properties: FunnelEventMap[K],
): void {
  try {
    const posthog = getPostHogClient();
    posthog?.capture(event, properties as Record<string, unknown>);
  } catch {
    // Analytics failure MUST NOT break user flow — swallow silently.
  }
}
